import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode,
} from 'react';
import {
  element,
  findElementById,
  removeElements,
  rootElement,
  setAttr,
  type XElement,
} from '../lib/xml';
import { parseXml, serializeXml, XmlParseError } from '../lib/xml';
import {
  defaultPointAttrs,
  detectKind,
  extractFeatures,
  fmt,
  newDocId,
  newGroupElement,
  pointTagFor,
  setEconomyField,
  setRowAttr,
  toggleEconomyTag,
  withDoc,
  type EconomyTag,
  type EditorDoc,
  type Feature,
  type FeatureGroup,
} from '../lib/model';
import { DEFAULT_IZURVIVE_VERSION, MAP_PRESETS, presetById } from '../lib/maps';
import type { StoredSettings } from '../lib/storage';

export type Tool = 'select' | 'point' | 'zone' | 'scatter' | 'erase' | 'measure';

export type Settings = StoredSettings;

export type State = {
  docs: EditorDoc[];
  selection: string[];
  activeDocId: string | null;
  activeGroupId: string | null;
  tool: Tool;
  mapId: string;
  mapSize: number;
  settings: Settings;
  past: EditorDoc[][];
  future: EditorDoc[][];
  notice: { kind: 'info' | 'error'; text: string } | null;
  dirty: boolean;
};

export const LAYER_COLORS = [
  '#e5484d', '#3b9eff', '#46a758', '#f5a524', '#a457f5', '#00b8d9', '#ff8b3d', '#d6409f',
];

export const DEFAULT_SETTINGS: Settings = {
  showGrid: true,
  showLabels: true,
  snap: 0,
  brushRadius: 120,
  brushSpacing: 45,
  defaultZoneRadius: 300,
  basemap: 'official',
  izurviveVersion: DEFAULT_IZURVIVE_VERSION,
  showTiers: false,
  showBuildings: true,
};

export const initialState: State = {
  docs: [],
  selection: [],
  activeDocId: null,
  activeGroupId: null,
  tool: 'select',
  mapId: MAP_PRESETS[0].id,
  mapSize: MAP_PRESETS[0].size,
  settings: DEFAULT_SETTINGS,
  past: [],
  future: [],
  notice: null,
  dirty: false,
};

export type Action =
  | { type: 'loadFiles'; files: { fileName: string; content: string; visible?: boolean; color?: string; locked?: boolean }[]; replace?: boolean }
  | { type: 'removeDoc'; docId: string }
  | { type: 'patchDoc'; docId: string; patch: Partial<Pick<EditorDoc, 'visible' | 'color' | 'locked' | 'fileName'>> }
  | { type: 'setActive'; docId: string | null; groupId?: string | null }
  | { type: 'setTool'; tool: Tool }
  | { type: 'setSelection'; ids: string[]; mode?: 'replace' | 'add' | 'toggle' }
  | { type: 'addPoints'; docId: string; groupId: string; points: { x: number; z: number; r?: number }[] }
  | { type: 'moveSelection'; dx: number; dz: number }
  | { type: 'setFeatureAttrs'; ids: string[]; attrs: Record<string, string> }
  | { type: 'deleteSelection' }
  | { type: 'addGroup'; docId: string; name: string }
  | { type: 'removeGroup'; docId: string; groupId: string }
  | { type: 'setGroupAttr'; docId: string; groupId: string; name: string; value: string }
  | { type: 'economySetField'; docId: string; rowId: string; field: string; value: string }
  | { type: 'economyToggleTag'; docId: string; rowId: string; tag: EconomyTag; name: string }
  | { type: 'economySetAttr'; docId: string; rowId: string; name: string; value: string }
  | { type: 'setMap'; mapId: string; mapSize?: number }
  | { type: 'setSettings'; patch: Partial<Settings> }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'notice'; kind: 'info' | 'error'; text: string }
  | { type: 'dismissNotice' }
  | { type: 'clearAll' }
  | { type: 'markSaved' };

const MAX_HISTORY = 100;

function commit(state: State, docs: EditorDoc[], extra: Partial<State> = {}): State {
  return {
    ...state,
    docs,
    past: [...state.past, state.docs].slice(-MAX_HISTORY),
    future: [],
    dirty: true,
    ...extra,
  };
}

/** Applies a mutation to one document, returning the new document list. */
function mutateDoc(state: State, docId: string, mutate: (root: XElement, doc: EditorDoc) => void): EditorDoc[] {
  return state.docs.map((doc) => {
    if (doc.id !== docId) return doc;
    return withDoc(doc, (copy) => {
      const root = rootElement(copy);
      if (root) mutate(root, doc);
    });
  });
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'loadFiles': {
      const docs: EditorDoc[] = action.replace ? [] : [...state.docs];
      const errors: string[] = [];
      let added = 0;
      for (const file of action.files) {
        try {
          const parsed = parseXml(file.content);
          const kind = detectKind(parsed);
          const existing = docs.findIndex((d) => d.fileName === file.fileName);
          const doc: EditorDoc = {
            id: newDocId(),
            fileName: file.fileName,
            kind,
            doc: parsed,
            visible: file.visible ?? true,
            color: file.color ?? LAYER_COLORS[(docs.length + added) % LAYER_COLORS.length],
            locked: file.locked ?? false,
          };
          if (existing >= 0) docs[existing] = doc;
          else docs.push(doc);
          added += 1;
        } catch (error) {
          const reason = error instanceof XmlParseError ? error.message : 'could not be parsed';
          errors.push(`${file.fileName}: ${reason}`);
        }
      }
      const firstEditable = docs.find((d) => d.kind !== 'events' && d.kind !== 'types');
      return {
        ...state,
        docs,
        selection: [],
        activeDocId: state.activeDocId && docs.some((d) => d.id === state.activeDocId)
          ? state.activeDocId
          : (firstEditable ?? docs[0])?.id ?? null,
        activeGroupId: null,
        past: [...state.past, state.docs].slice(-MAX_HISTORY),
        future: [],
        dirty: true,
        notice: errors.length
          ? { kind: 'error', text: `Could not load ${errors.length} file(s): ${errors.join('; ')}` }
          : { kind: 'info', text: `Loaded ${added} file(s).` },
      };
    }

    case 'removeDoc': {
      const docs = state.docs.filter((d) => d.id !== action.docId);
      return commit(state, docs, {
        activeDocId: state.activeDocId === action.docId ? (docs[0]?.id ?? null) : state.activeDocId,
        activeGroupId: null,
        selection: [],
      });
    }

    case 'patchDoc':
      return {
        ...state,
        docs: state.docs.map((d) => (d.id === action.docId ? { ...d, ...action.patch } : d)),
        dirty: true,
      };

    case 'setActive':
      return {
        ...state,
        activeDocId: action.docId,
        activeGroupId: action.groupId !== undefined ? action.groupId : state.activeGroupId,
      };

    case 'setTool':
      return { ...state, tool: action.tool };

    case 'setSelection': {
      const mode = action.mode ?? 'replace';
      if (mode === 'replace') return { ...state, selection: action.ids };
      if (mode === 'add') return { ...state, selection: [...new Set([...state.selection, ...action.ids])] };
      const next = new Set(state.selection);
      for (const id of action.ids) {
        if (next.has(id)) next.delete(id);
        else next.add(id);
      }
      return { ...state, selection: [...next] };
    }

    case 'addPoints': {
      const target = state.docs.find((d) => d.id === action.docId);
      if (!target || target.locked) return state;
      const created: string[] = [];
      const docs = mutateDoc(state, action.docId, (root) => {
        const group = findElementById({ nodes: [root] }, action.groupId) ?? root;
        for (const point of action.points) {
          const attrs = defaultPointAttrs(
            target.kind,
            point.x,
            point.z,
            point.r ?? state.settings.defaultZoneRadius,
          );
          if (target.kind === 'territories') {
            attrs.name = `${nameSeed(group)}${group.children.length + 1}`;
          }
          const el = element(pointTagFor(target.kind), attrs);
          group.children.push(el);
          created.push(el.id);
        }
      });
      return commit(state, docs, { selection: created });
    }

    case 'moveSelection': {
      if (state.selection.length === 0) return state;
      const ids = new Set(state.selection);
      const docs = state.docs.map((doc) => {
        if (doc.locked) return doc;
        let touched = false;
        const next = withDoc(doc, (copy) => {
          for (const id of ids) {
            const el = findElementById(copy, id);
            if (!el) continue;
            const x = Number.parseFloat(el.attrs.find((a) => a.name === 'x')?.value ?? '0');
            const z = Number.parseFloat(el.attrs.find((a) => a.name === 'z')?.value ?? '0');
            setAttr(el, 'x', fmt(x + action.dx));
            setAttr(el, 'z', fmt(z + action.dz));
            touched = true;
          }
        });
        return touched ? next : doc;
      });
      return commit(state, docs);
    }

    case 'setFeatureAttrs': {
      const ids = new Set(action.ids);
      const docs = state.docs.map((doc) => {
        if (doc.locked) return doc;
        let touched = false;
        const next = withDoc(doc, (copy) => {
          for (const id of ids) {
            const el = findElementById(copy, id);
            if (!el) continue;
            for (const [name, value] of Object.entries(action.attrs)) setAttr(el, name, value);
            touched = true;
          }
        });
        return touched ? next : doc;
      });
      return commit(state, docs);
    }

    case 'deleteSelection': {
      if (state.selection.length === 0) return state;
      const ids = new Set(state.selection);
      const docs = state.docs.map((doc) => {
        if (doc.locked) return doc;
        let removed = 0;
        const next = withDoc(doc, (copy) => {
          removed = removeElements(copy, ids);
        });
        return removed > 0 ? next : doc;
      });
      return commit(state, docs, { selection: [] });
    }

    case 'addGroup': {
      const target = state.docs.find((d) => d.id === action.docId);
      if (!target) return state;
      let newId: string | null = null;
      const docs = mutateDoc(state, action.docId, (root) => {
        const group = newGroupElement(target.kind, action.name);
        newId = group.id;
        root.children.push(group);
      });
      return commit(state, docs, { activeDocId: action.docId, activeGroupId: newId });
    }

    case 'removeGroup': {
      const docs = state.docs.map((doc) =>
        doc.id === action.docId
          ? withDoc(doc, (copy) => {
              removeElements(copy, new Set([action.groupId]));
            })
          : doc,
      );
      return commit(state, docs, {
        activeGroupId: state.activeGroupId === action.groupId ? null : state.activeGroupId,
        selection: [],
      });
    }

    case 'setGroupAttr': {
      const docs = mutateDoc(state, action.docId, (root) => {
        const group = findElementById({ nodes: [root] }, action.groupId);
        if (group) setAttr(group, action.name, action.value);
      });
      return commit(state, docs);
    }

    case 'economySetField': {
      const docs = mutateDoc(state, action.docId, (root) => {
        setEconomyField(root, action.rowId, action.field, action.value);
      });
      return commit(state, docs);
    }

    case 'economyToggleTag': {
      const docs = mutateDoc(state, action.docId, (root) => {
        toggleEconomyTag(root, action.rowId, action.tag, action.name);
      });
      return commit(state, docs);
    }

    case 'economySetAttr': {
      const docs = mutateDoc(state, action.docId, (root) => {
        setRowAttr(root, action.rowId, action.name, action.value);
      });
      return commit(state, docs);
    }

    case 'setMap': {
      const preset = presetById(action.mapId);
      return {
        ...state,
        mapId: action.mapId,
        mapSize: action.mapSize ?? preset.size,
        dirty: true,
      };
    }

    case 'setSettings':
      return { ...state, settings: { ...state.settings, ...action.patch } };

    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        ...state,
        docs: previous,
        past: state.past.slice(0, -1),
        future: [state.docs, ...state.future].slice(0, MAX_HISTORY),
        selection: [],
        dirty: true,
      };
    }

    case 'redo': {
      const [next, ...rest] = state.future;
      if (!next) return state;
      return {
        ...state,
        docs: next,
        past: [...state.past, state.docs].slice(-MAX_HISTORY),
        future: rest,
        selection: [],
        dirty: true,
      };
    }

    case 'notice':
      return { ...state, notice: { kind: action.kind, text: action.text } };

    case 'dismissNotice':
      return { ...state, notice: null };

    case 'clearAll':
      return { ...initialState, settings: state.settings, mapId: state.mapId, mapSize: state.mapSize };

    case 'markSaved':
      return { ...state, dirty: false };

    default:
      return state;
  }
}

function nameSeed(group: XElement): string {
  const name = group.attrs.find((a) => a.name === 'name')?.value;
  return name ? `${name}_` : 'Zone';
}

/* ------------------------------------------------------------------ */

const StateContext = createContext<State>(initialState);
const DispatchContext = createContext<Dispatch<Action>>(() => undefined);

export function StoreProvider({ children, preloaded }: { children: ReactNode; preloaded?: State }) {
  const [state, dispatch] = useReducer(reducer, preloaded ?? initialState);
  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
}

export function useStore(): State {
  return useContext(StateContext);
}

export function useDispatch(): Dispatch<Action> {
  return useContext(DispatchContext);
}

/** All features of all visible documents, plus a lookup by element id. */
export function useFeatures(): {
  features: Feature[];
  groups: FeatureGroup[];
  byId: Map<string, Feature>;
} {
  const { docs } = useStore();
  return useMemo(() => {
    const features: Feature[] = [];
    const groups: FeatureGroup[] = [];
    for (const doc of docs) {
      const extracted = extractFeatures(doc);
      features.push(...extracted.features);
      groups.push(...extracted.groups);
    }
    return { features, groups, byId: new Map(features.map((f) => [f.id, f])) };
  }, [docs]);
}

export function serializeDoc(doc: EditorDoc): string {
  return serializeXml(doc.doc);
}
